import { useEffect, useCallback, useState } from 'react'
import { Popover } from 'antd'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'
import classNames from 'classnames'
import styles from './MessageEntry.module.css'

import { useStores } from '~/hooks/useStores'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { MyIcon } from '~/components/ui/MyIcon'
import {
  MessageCard,
  unreadInfoItem,
  primaryMsgType,
  filterMsgType,
} from './MessageCard'
import { MessageList } from './MessageList'
import { useMsg } from './useMsg'

import { GET_UNREAD_MESSAGE_COUNT_LIST } from '~/graphqls/gqls'
import {
  BaseMsgType,
  getUnreadMessageCountList,
  getUnreadMessageCountListVariables,
} from '~/graphqls/types'
const StyledWrap = styled.div`
  margin-left: 24px;
  .msg-icon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    user-select: none;
    cursor: pointer;
    transition: all 0.3s ease;
    color: ${props => props.theme['neutral-400']};
    &:hover {
      transition: all 0.3s ease;
      color: ${props => props.theme.primary};
    }
  }
  .msg-unread {
    position: absolute;
    left: 12px;
    top: -4px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 24px;
    padding: 0 6px;
    border-radius: 180px;
    background: ${props => props.theme.error};
    color: ${props => props.theme['neutral-800']};
    font-weight: 600;
    font-size: 16px;
    transform: scale(0.5);
    transform-origin: left top;
  }
`

/** 消息通知入口 */
export const MessageEntry: React.FC = observer(() => {
  const [visible, setVisible] = useState(false)
  // 一级消息类型
  const [primaryMsg, setPrimaryMsg] = useState<unreadInfoItem[]>()
  // 系统子消息类型
  const [systemMsg, setSystemMsg] = useState<unreadInfoItem[]>()
  // 激活的消息对象
  const [activeMsgItem, setActiveMsgItem] = useState<unreadInfoItem>()
  // 系统消息列表
  const [showList, setShowList] = useState(false)

  const { store } = useStores()
  const { handleUnreadCount } = useMsg()

  /** 未读消息列表 */
  const getUnReadList = useImperativeQuery<
    getUnreadMessageCountList,
    getUnreadMessageCountListVariables
  >(GET_UNREAD_MESSAGE_COUNT_LIST)

  /** 获取未读消息列表 */
  const getHasUnreadMsg = useCallback(async () => {
    try {
      const resp = await getUnReadList()
      const list = resp?.data?.unreadCountList
      if (!!list?.length) {
        const primaryList: unreadInfoItem[] = []
        const systemList: unreadInfoItem[] = []
        list?.forEach(it => {
          // 一级消息类型
          if (primaryMsgType.includes(it.baseMsgType as BaseMsgType)) {
            primaryList.push(it)
            // 过滤评论后的系统消息类型
          } else if (!filterMsgType.includes(it.baseMsgType as BaseMsgType)) {
            systemList.push(it)
          }
        })
        setPrimaryMsg(primaryList)
        setSystemMsg(systemList)
      }
    } catch (error) {}
  }, [getUnReadList])

  /** 重置具体项数量，默认：read成功则扣减数量 */
  const resetTypeCount = useCallback(
    (type: BaseMsgType) => {
      if (primaryMsgType.includes(type)) {
        // 主消息类型则关闭弹窗
        setVisible(false)
      } else {
        // 需要扣减的消息数量
        let singleCount = 0
        setSystemMsg(list => {
          return list?.map(it => {
            const count = it?.count as number
            if (it.baseMsgType === type && count > 0) {
              // 只有非屏蔽状态下才扣减总数量
              if (!it.shield) {
                singleCount = count
              }

              // 单个类型数量重置
              it.count = 0
            }
            return {
              ...it,
            }
          })
        })
        // 当存在扣减数量时，全局扣减数量
        singleCount > 0 &&
          store?.setMsgCount((store.msgCount as number) - singleCount)
      }
    },
    [setSystemMsg]
  )
  // 初始化
  const init = () => {
    getHasUnreadMsg()
    handleUnreadCount()
  }
  /** 重置选项 */
  const reset = () => {
    setTimeout(() => {
      setShowList(false)
    }, 500)
  }

  /** 定时获取是否有未读消息 */
  useEffect(() => {
    handleUnreadCount()

    // 页面跳转会重置定时器
    const timer = setInterval(() => {
      handleUnreadCount()
    }, 60 * 1000)

    return () => {
      clearInterval(timer)
    }
  }, [])

  return (
    <StyledWrap>
      <Popover
        destroyTooltipOnHide={true}
        getPopupContainer={(triggerNode: HTMLElement) =>
          triggerNode.parentNode as HTMLElement
        }
        placement="bottomRight"
        trigger={['click']}
        open={visible}
        content={
          <div className={classNames(styles.wrapper, 'scrollbar-hidden')}>
            <MessageCard
              primaryMsg={primaryMsg}
              systemMsg={systemMsg}
              onClickMsg={msgItem => {
                setActiveMsgItem(msgItem)
                setShowList(true)
              }}
              onReadMsg={msgItem => {
                resetTypeCount(msgItem?.baseMsgType as BaseMsgType)
              }}
              onReadAllMsg={() => {
                init()
              }}
              className={classNames('absolute ', showList ? 'opacity-0' : '')}
            />
            {showList && (
              <MessageList
                className="absolute z-10 "
                activeMsgItem={activeMsgItem}
                onListChange={() => {
                  setShowList(false)
                }}
              />
            )}
          </div>
        }
        onOpenChange={open => {
          setVisible(open)
          open ? init() : reset()
        }}
        overlayClassName={classNames(
          'shodow-dm rounded-xl',
          styles.hiddenPopoverArrow
        )}
      >
        <div className="msg-icon">
          <MyIcon icon="information" className="text-[20px]" />
          {!!store?.msgCount && store?.msgCount > 0 ? (
            <span className="msg-unread">
              {store?.msgCount > 99 ? '99+' : store?.msgCount}
            </span>
          ) : null}
        </div>
      </Popover>
    </StyledWrap>
  )
})
